const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';
const CASCADE_GROUPS = new WeakMap();

/* =========================
   TEXT REVEAL — helpers
   ========================= */
function splitTextReveal(el) {
  if (!el || el.dataset.split === 'true') return;

  const raw = (el.textContent || '').trim();
  if (!raw) return;

  // Build accessible structure:
  // We keep visible words but mark the generated wrapper as aria-hidden,
  // and mirror full text as aria-label so SRs read clean text once.
  el.setAttribute('aria-label', raw);

  const words = raw.split(/\s+/);
  const html = words
    .map(w => `<span class="text-reveal-word" aria-hidden="true"><span>${w}</span></span>`)
    .join(' ');

  el.innerHTML = html;
  el.dataset.split = 'true';
}

function animateTextReveal(el) {
  if (!el || el.dataset.animated === 'true') return;

  // If reduced motion, just mark as animated and bail
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const spans = el.querySelectorAll('.text-reveal-word > span');
  if (!spans.length) return;

  if (prefersReduced || typeof gsap === 'undefined') {
    spans.forEach(s => (s.style.transform = 'none'));
    el.dataset.animated = 'true';
    return;
  }

  gsap.fromTo(
    spans,
    { yPercent: 100, skewY: 7 },
    {
      yPercent: 0,
      skewY: 0,
      duration: 1.1,
      ease: 'power4.out',
      stagger: { amount: 0.35 }
    }
  );

  el.dataset.animated = 'true';
}

/* =========================
   SCROLL IN — your logic (with reveal hook)
   ========================= */
function onIntersection(entries, observer) {
  entries.forEach((entry, index) => {
    const target = entry.target;

    if (entry.isIntersecting) {
      // Un-hide scroll-trigger item
      if (target.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        target.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (target.hasAttribute('data-cascade')) {
          let order = target.getAttribute('data-cascade-order');
          if (order === null) {
            const parent = target.parentElement;
            if (parent) {
              const nextOrder = CASCADE_GROUPS.get(parent) || 0;
              CASCADE_GROUPS.set(parent, nextOrder + 1);
              order = String(nextOrder);
            } else {
              order = String(index);
            }
          }
          target.style.setProperty('--animation-order', order);
        }
      }

      // Text reveal: if the element itself is .text-reveal, or it contains any .text-reveal children
      if (target.classList.contains('text-reveal')) {
        splitTextReveal(target);
        animateTextReveal(target);
      } else {
        target.querySelectorAll('.text-reveal').forEach(el => {
          splitTextReveal(el);
          animateTextReveal(el);
        });
      }

      observer.unobserve(target);
    } else {
      // Element left the viewport (rarely needed when unobserving on enter)
      target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const nodes = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (!nodes.length) return;

  if (isDesignModeEvent) {
    nodes.forEach((el) => el.classList.add('scroll-trigger--design-mode'));
    // In editor, pre-split so text looks correct while designing
    nodes.forEach((el) => {
      if (el.classList.contains('text-reveal')) splitTextReveal(el);
      el.querySelectorAll('.text-reveal').forEach(splitTextReveal);
    });
    return;
  }

  // Pre-split all text-reveal nodes we know about now
  nodes.forEach((el) => {
    if (el.classList.contains('text-reveal')) splitTextReveal(el);
    el.querySelectorAll('.text-reveal').forEach(splitTextReveal);
  });

  const observer = new IntersectionObserver(onIntersection, { rootMargin: '0px 0px -50px 0px' });
  nodes.forEach((el) => observer.observe(el));
}

/* =========================
   LEGACY zoom code (unchanged)
   ========================= */
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const els = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));
  if (!els.length) return;

  const scaleAmount = 0.2 / 100;

  els.forEach((element) => {
    let visible = false;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => (visible = e.isIntersecting));
    });
    io.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!visible) return;
        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const vh = window.innerHeight;
  const y = window.scrollY;
  const top = element.getBoundingClientRect().top + y;
  const h = element.offsetHeight;

  if (top > y + vh) return 0;
  if (top + h < y) return 100;

  const dist = y + vh - top;
  return Math.round(dist / ((vh + h) / 100));
}

/* =========================
   BOOT
   ========================= */
window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

/* Theme Editor support */
if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => initializeScrollAnimationTrigger(e.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
